import { PointConfigExtended } from '@src/types/PointConfig';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import storeConnector from '@store/storeConnector';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColType,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import useGetPointConfigs from '@src/hooks/api/queries/collections/useGetPointConfigs';
import { CollectionPayload, CollectionTypes } from '@src/types/Collection';
import { isActionRole } from '@src/services/auth';
import { UserRole } from '@src/services/auth/roles';
import { GridBaseColDef } from '@mui/x-data-grid/internals';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Snackbar,
  SnackbarCloseReason,
  Typography,
} from '@mui/material';
import deviceSx from '@src/components/Home/Collections/configs/DeviceSx';
import getCollectionsInitialState from '@src/components/Home/Collections/utils/getCollectionsInitialState';
import CollectionToolbar from '@src/components/Home/Collections/CollectionToolbar';
import PointConfigDialog from '@src/components/Home/Collections/PointConfigs/PointConfigDialog';
import pointConfigBaseColumns from '@src/components/Home/Collections/configs/pointConfigBaseColumns';
import CollectionWrapper from '../CollectionWrapper';
import usePointConfigMutations from '@src/hooks/api/mutations/usePointConfigMutations';
import { useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@src/constants/queryKeys';
import useAlias from '@alias';

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides {
    collectionType?: CollectionTypes;
    setOpen: Dispatch<SetStateAction<boolean>>;
    setJsonPayload: Dispatch<SetStateAction<CollectionPayload | null>>;
    onSyncInflux?: () => void;
    isSyncing?: boolean;
  }
}

function PointConfigCollection({ role }: { role: UserRole }) {
  const alias = useAlias();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [rows, setRows] = useState<PointConfigExtended[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rowSelectionModel, setRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();

  const [jsonPayload, setJsonPayload] = useState<CollectionPayload | null>(
    null,
  );
  const { data, isFetching, isPending, isSuccess, error, isRefetching } =
    useGetPointConfigs();

  const { syncFromInflux } = usePointConfigMutations();

  const handleClose = (
    _: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setConfirmOpen(false);
    setSyncMessage('');
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    if (apiRef.current !== undefined) {
      setJsonPayload(apiRef.current.getRow(id) as CollectionPayload);
    }
    setOpen(true);
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    if (apiRef.current !== undefined) {
      setJsonPayload({
        ...(apiRef.current.getRow(id) as CollectionPayload),
        remove: true,
      });
    }
    setOpen(true);
  };

  const handleCancelClick = (id?: GridRowId) => () => {
    if (id !== undefined) {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });
    }
    setOpen(false);
    setJsonPayload(null);
  };

  const handleSyncFromInflux = () => {
    syncFromInflux.mutate(
      {},
      {
        onSuccess: () => {
          setSyncMessage(
            alias('Point configs synced from Influx successfully'),
          );
          setConfirmOpen(true);
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.pointConfigs],
          });
        },
        onError: (error) => {
          console.error('Failed to sync point configs from Influx:', error);
          setSyncMessage(alias('Failed to sync point configs from Influx'));
          setConfirmOpen(true);
        },
      },
    );
  };

  const columns = [
    ...pointConfigBaseColumns(alias),
    ...(isActionRole(role) && !process.env.VITE_READ_ONLY_MODE
      ? [
          {
            field: 'actions',
            type: 'actions' as GridColType,
            headerName: alias('Actions'),
            description: alias('Actions'),
            width: 120,
            cellClassName: 'actions',
            getActions: ({ id }: GridRowParams<{ id: string }>) => [
              <GridActionsCellItem
                key='edit'
                icon={<EditIcon />}
                label={alias('Edit')}
                className='textPrimary'
                onClick={handleEditClick(id)}
                color='inherit'
              />,
              <GridActionsCellItem
                key='delete'
                icon={<DeleteIcon />}
                label={alias('Delete')}
                onClick={handleDeleteClick(id)}
                color='inherit'
              />,
            ],
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (error) {
      console.error(error);
    }
    if (isSuccess && data !== null) {
      setRows(data.map((device) => ({ ...device, id: device.id })));
    }
  }, [isSuccess, data, error]);

  return (
    <CollectionWrapper>
      <div className='cell block-container flex-1 overflow-y-auto'>
        <Snackbar
          open={confirmOpen}
          autoHideDuration={4000}
          onClose={handleClose}
          sx={deviceSx.confirm}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleClose}
            severity={syncMessage.includes('Failed') ? 'error' : 'success'}
            variant='filled'
            sx={deviceSx.confirm.alert}
          >
            {syncMessage || alias('Point config changes saved')}
          </Alert>
        </Snackbar>
        <Box sx={{ position: 'relative', height: '100%' }}>
          <DataGridPro
            rows={rows}
            apiRef={apiRef}
            checkboxSelection
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            onRowSelectionModelChange={setRowSelectionModel}
            rowSelectionModel={rowSelectionModel}
            initialState={getCollectionsInitialState()}
            columns={columns as GridBaseColDef[]}
            getRowId={({ pointAlias }) => pointAlias}
            loading={!isRefetching && (isFetching || isPending)}
            slots={{
              toolbar: CollectionToolbar,
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'skeleton',
                noRowsVariant: 'skeleton',
              },
              toolbar: {
                collectionType: CollectionTypes.POINT_CONFIGS,
                setOpen,
                setJsonPayload,
                onSyncInflux: handleSyncFromInflux,
                isSyncing: syncFromInflux.isPending,
              },
            }}
            localeText={{
              toolbarColumns: alias('Columns'),
              toolbarFilters: alias('Filters'),
              toolbarExport: alias('Export'),
              toolbarQuickFilterPlaceholder: alias('Search...'),
            }}
          />
          {syncFromInflux.isPending && (
            <Backdrop
              open={true}
              sx={{
                position: 'absolute',
                zIndex: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={60} />
              <Typography variant='h6' sx={{ color: 'text.primary' }}>
                {alias('Syncing with Influx...')}
              </Typography>
            </Backdrop>
          )}
        </Box>

        {open && (
          <PointConfigDialog
            jsonData={jsonPayload as PointConfigExtended}
            setOpen={setOpen}
            handleCancel={handleCancelClick}
            setRowModesModel={setRowModesModel}
            setJsonPayload={setJsonPayload}
          />
        )}
      </div>
    </CollectionWrapper>
  );
}

export default storeConnector(PointConfigCollection, {
  user: ['role'],
});
